import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";
import { environment } from "../../../environments/environment";

export interface PurifaiMiSubmoduleType {
  code: string;
  label: string;
  hidden: boolean;
}

export interface PurifaiMiModuleType {
  code: string;
  label: string;
  category: string | null;
  displayOrder: number | null;
  hidden: boolean;
  submoduleTypes: PurifaiMiSubmoduleType[];
}

export interface PurifaiMiCatalogue {
  moduleTypes: PurifaiMiModuleType[];
}

/** One of PurifAI's own modules that a prefill was drawn from. */
export interface PurifaiMiPrefillModule {
  code: string;
  name: string;
  module_type: string | null;
}

/**
 * What PurifAI already has for the unit, sent only while nothing is saved in PureBI.
 * `mi_module_type` null with modules listed means PurifAI's modules disagree.
 */
export interface PurifaiMiPrefill {
  mi_module_type: string | null;
  mi_submodule_kinds: string[];
  purifai_modules: PurifaiMiPrefillModule[];
}

/** What is stored for one site module unit: PurifAI codes, never labels. */
export interface SitePurifaiMi {
  mi_module_type: string | null;
  mi_submodule_kinds: string[];
  /**
   * The stored Module Intelligence toggle. null = never decided, in which case the
   * dialog — and PurifAI's feed — go by whether the unit has any config.
   */
  module_intelligence_enabled?: boolean | null;
  /** Read responses only. Never sent on save. */
  prefill?: PurifaiMiPrefill | null;
}

/**
 * A save of the module choice, the toggle, or both. A key left out leaves that
 * part unchanged on the server.
 */
export interface SitePurifaiMiSave {
  site_id: string | number;
  module_code: string | number;
  mi_module_type?: string | null;
  mi_submodule_kinds?: string[];
  module_intelligence_enabled?: boolean;
}

interface ApiResponse<T> {
  status: number;
  msg?: string;
  data?: T;
}

/**
 * PurifAI Module Intelligence for a site module: PurifAI's catalogue of module
 * types and submodule types, and the codes chosen for one unit.
 *
 * PurifAI owns the catalogue — PureBI only reads it, through a server-side proxy
 * that keeps PurifAI's API key off the browser.
 *
 * Not PureBI's own "Module Type" (lkp_module_type), which is unrelated.
 */
@Injectable({ providedIn: "root" })
export class PurifaiMiService {
  /**
   * Shared for the life of the page, so reopening the Configure dialog does not
   * refetch. Dropped on failure, so the next dialog open tries again rather than
   * replaying the error.
   */
  private catalogue$: Observable<PurifaiMiCatalogue> | null = null;

  constructor(private http: HttpClient) {}

  getCatalogue(): Observable<PurifaiMiCatalogue> {
    if (!this.catalogue$) {
      this.catalogue$ = this.http
        .post<ApiResponse<PurifaiMiCatalogue>>(`${environment.nodeUrl1}getPurifaiMiCatalogue`, {})
        .pipe(
          map(response => {
            if (response?.status !== 1 || !Array.isArray(response.data?.moduleTypes)) {
              throw new Error(response?.msg || "PurifAI module list unavailable");
            }
            return response.data;
          }),
          catchError(error => {
            this.catalogue$ = null;
            return throwError(() => error);
          }),
          shareReplay(1)
        );
    }
    return this.catalogue$;
  }

  getSaved(siteId: string | number, moduleCode: string | number): Observable<SitePurifaiMi> {
    return this.http
      .post<ApiResponse<SitePurifaiMi>>(`${environment.nodeUrl1}getSiteModulePurifaiMi`, {
        site_id: siteId,
        module_code: moduleCode,
      })
      .pipe(map(response => this.unwrap(response)));
  }

  /** Errors with the server's message when it refuses the save (status 0). */
  save(request: SitePurifaiMiSave): Observable<SitePurifaiMi> {
    return this.http
      .post<ApiResponse<SitePurifaiMi>>(`${environment.nodeUrl1}saveSiteModulePurifaiMi`, request)
      .pipe(map(response => this.unwrap(response)));
  }

  private unwrap(response: ApiResponse<SitePurifaiMi>): SitePurifaiMi {
    if (response?.status !== 1 || !response.data) {
      throw new Error(response?.msg || "PurifAI Module Intelligence request failed");
    }
    return {
      mi_module_type: response.data.mi_module_type || null,
      mi_submodule_kinds: Array.isArray(response.data.mi_submodule_kinds)
        ? [...response.data.mi_submodule_kinds]
        : [],
      module_intelligence_enabled: typeof response.data.module_intelligence_enabled === "boolean"
        ? response.data.module_intelligence_enabled
        : null,
      prefill: this.toPrefill(response.data.prefill),
    };
  }

  private toPrefill(value: any): PurifaiMiPrefill | null {
    const modules = (Array.isArray(value?.purifai_modules) ? value.purifai_modules : [])
      .filter((module: any) => module && typeof module.name === "string");
    if (!modules.length) {
      return null;
    }
    return {
      mi_module_type: typeof value.mi_module_type === "string" && value.mi_module_type
        ? value.mi_module_type
        : null,
      mi_submodule_kinds: (Array.isArray(value.mi_submodule_kinds) ? value.mi_submodule_kinds : [])
        .filter((code: unknown) => typeof code === "string"),
      purifai_modules: modules.map((module: any) => ({
        code: String(module.code ?? ""),
        name: module.name,
        module_type: typeof module.module_type === "string" ? module.module_type : null,
      })),
    };
  }
}
